import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import Box from 'carbon-react/lib/components/box';
import Message from 'carbon-react/lib/components/message';
import Textbox from 'carbon-react/lib/components/textbox';
import Typography from 'carbon-react/lib/components/typography';

import { ERROR_STATUS } from '@futrli/errors';
import {
  FooterMode,
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { getReportingEditReportUrl } from '@futrli/shared-services';
import { LiveReportList } from '@futrli-sdk/predict';
import { LoadingWizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/loading-wizard-screen/loading-wizard-screen';
import { ModifyReportView } from '@futrli/views/views/reports/ModifyReportView/ModifyReportView';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { WizardFinished } from '@futrli/ui-components/components/sidebar/wizard/wizard-finished/wizard-finished';
import { WizardFormError } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-error/wizard-form-error';
import { WizardFormValidationErrorSummary } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-validation-error-summary/wizard-form-validation-error-summary';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';

import { useRouteIds } from '../../../hooks/useRouteIds';

export interface DuplicateReportWizardContainerProps {
  onClose: () => void;
  report: LiveReportList;
}

type Screen = 'form' | 'duplicating' | 'duplicated';

const FIELD_ID = 'duplicate-report-name';

const getInaccessibleForecastIds = (error: Error | null): string[] => {
  const response = (error as
    | (Error & { response?: { status?: number; data?: { inaccessible_forecast_ids?: unknown } } })
    | null)?.response;
  const forecastIds = response?.data?.inaccessible_forecast_ids;

  return response?.status === ERROR_STATUS._403 && Array.isArray(forecastIds)
    ? forecastIds.filter((forecastId): forecastId is string => typeof forecastId === 'string')
    : [];
};

export const DuplicateReportWizardContainer = ({ onClose, report }: DuplicateReportWizardContainerProps) => {
  const navigate = useNavigate();
  const intl = useIntl();

  const { workspaceId, organisationId, isConsolidation } = useRouteIds();

  const view = useApiView(
    useMemo(() => new ModifyReportView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const [screen, setScreen] = useState<Screen>('form');
  const copyLabel = intl.formatMessage({ id: 'common.copy', defaultMessage: 'Copy' });
  const [reportName, setReportName] = useState(`[${copyLabel}] ${report.name}`);
  const [error, setError] = useState<string | undefined>();
  const [validationErrors, setValidationErrors] = useState<Record<string, { id: string; message: string }>>({});
  const hasBeenValidated = useRef(false);
  const inaccessibleForecastIds = useMemo(() => getInaccessibleForecastIds(view.state.errorDuplicating), [
    view.state.errorDuplicating
  ]);

  const validate = useCallback(() => {
    const errors: Record<string, { id: string; message: string }> = {};

    if (!reportName.trim()) {
      errors[FIELD_ID] = {
        id: FIELD_ID,
        message: intl.formatMessage({
          id: 'reporting.validation.report_name_is_required',
          defaultMessage: 'Report name is required'
        })
      };
    }

    setValidationErrors(errors);
    hasBeenValidated.current = true;
    return Object.keys(errors).length === 0;
  }, [reportName, intl]);

  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  useEffect(() => {
    if (view.state.isDuplicating) {
      setScreen('duplicating');
    }
  }, [view.state.isDuplicating]);

  useEffect(() => {
    if (view.state.duplicatedReportId) {
      setScreen('duplicated');
    }
  }, [view.state.duplicatedReportId]);

  useEffect(() => {
    if (view.state.errorDuplicating) {
      if (inaccessibleForecastIds.length) {
        setError(undefined);
        setScreen('form');
        return;
      }

      setError(intl.formatMessage({ id: 'reporting.try_again', defaultMessage: 'Try again.' }));
      setScreen('form');
    }
  }, [view.state.errorDuplicating, inaccessibleForecastIds, intl]);

  const handleSave = useCallback(() => {
    if (!validate()) {
      return;
    }

    setError(undefined);
    if (inaccessibleForecastIds.length) {
      setScreen('duplicating');
    }
    view.duplicateReport({
      reportId: report.id,
      reportName: reportName.trim(),
      ...(inaccessibleForecastIds.length ? { inaccessibleForecastIds } : {})
    });
  }, [validate, view, report.id, reportName, inaccessibleForecastIds]);

  const handleView = useCallback(() => {
    if (view.state.duplicatedReportId) {
      onClose();
      navigate(getReportingEditReportUrl(workspaceId, organisationId, view.state.duplicatedReportId, isConsolidation));
    }
  }, [view.state.duplicatedReportId, navigate, workspaceId, organisationId, isConsolidation, onClose]);

  if (screen === 'duplicating') {
    return (
      <LoadingWizardScreen
        text={intl.formatMessage({ id: 'reporting.creating_report', defaultMessage: 'Creating report...' })}
      />
    );
  }

  if (screen === 'duplicated') {
    return (
      <WizardScreen
        footerMode={FooterMode.CREATED}
        onCancel={onClose}
        onView={handleView}
        viewText={intl.formatMessage({ id: 'reporting.go_to_report', defaultMessage: 'Go to report' })}
      >
        <WizardFinished
          text={intl.formatMessage({
            id: 'reporting.report_created_and_saved_as_draft',
            defaultMessage: 'Report created and saved as a draft for review'
          })}
        />
      </WizardScreen>
    );
  }

  return (
    <WizardScreen
      footerMode={FooterMode.CREATE}
      onCancel={onClose}
      onCreate={handleSave}
      createText={intl.formatMessage({ id: 'common.duplicate', defaultMessage: 'Duplicate' })}
    >
      <>
        {!!inaccessibleForecastIds.length && (
          <Message
            open
            variant="warning"
            showCloseIcon={false}
            mb={3}
            title={
              <Typography variant="strong">
                {intl.formatMessage({
                  id: 'reporting.duplicate_report.limited_forecast_access_title',
                  defaultMessage: 'Limited forecast access'
                })}
              </Typography>
            }
          >
            {intl.formatMessage({
              id: 'reporting.duplicate_report.limited_forecast_access_message',
              defaultMessage:
                'You don’t have access to all forecasts used in this report. After duplicating it, you’ll need to update any sections that reference forecasts you can’t access.'
            })}
          </Message>
        )}

        {error && <WizardFormError message={error} mb={3} />}

        <WizardFormValidationErrorSummary errors={validationErrors} mb={3} />

        <WizardRequiredFieldInfo />

        <Box mb={3}>
          <Textbox
            label={intl.formatMessage({ id: 'reporting.report_name', defaultMessage: 'Report name' })}
            name={FIELD_ID}
            id={FIELD_ID}
            value={reportName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReportName(e.target.value)}
            onBlur={revalidate}
            error={validationErrors[FIELD_ID]?.message}
            required
          />
        </Box>
      </>
    </WizardScreen>
  );
};
